<template>
    <TransitionRoot as="template" :show="visible">
        <Dialog as="div" class="relative z-[100]" @close="close">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/70 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white px-6 pt-6 pb-6 text-left shadow-xl transition-all sm:my-8">
                            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                                <FontAwesomeIcon
                                    :icon="faTriangleExclamation"
                                    class="h-6 w-6 text-amber-600"
                                    aria-hidden="true"
                                />
                            </div>
                            <DialogTitle as="h3" class="mt-4 text-center text-lg font-semibold leading-6 text-gray-900">
                                Subscription canceled
                            </DialogTitle>
                            <p class="mt-4 text-sm leading-6 text-gray-700">
                                {{ message }}
                            </p>
                            <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                                <button
                                    type="button"
                                    class="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    @click="close"
                                >
                                    Dismiss
                                </button>
                                <a
                                    v-if="pricingUrl"
                                    :href="pricingUrl"
                                    class="inline-flex justify-center rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                                >
                                    Upgrade to Pro
                                </a>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'

const props = defineProps({
    warning: {
        type: Object,
        default: () => ({
            show: false,
            days_remaining: 0,
            pricing_url: null,
        }),
    },
})

const visible = ref(false)

const daysRemaining = computed(() => Math.max(0, Number(props.warning?.days_remaining ?? 0)))

const pricingUrl = computed(() => props.warning?.pricing_url ?? null)

const message = computed(() => {
    return 'Your subscription has been canceled. Your properties stay on your account on the free Basic plan. Upgrade to RiskGuard Pro to get rent payments and maintenance back.'
})

watch(
    () => props.warning,
    (warning) => {
        visible.value = Boolean(warning?.show && daysRemaining.value > 0)
    },
    { deep: true, immediate: true }
)

function close() {
    visible.value = false
}
</script>
